import React from "react";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { RadioButton } from "primereact/radiobutton";
import { InputNumber } from "primereact/inputnumber";
import moment from "moment";
import { provinces } from "../../../appconfig/Settings";
import { Tooltip } from "primereact/tooltip";

interface PersonalSectionProps {
  userData: any;
  errors: any;
  onInputChange: (e: any, name: string) => void;
  maxDate: Date;
  todayAsMaxDate: Date;
  isMarried: boolean;
}

const PersonalSection: React.FC<PersonalSectionProps> = ({ userData, errors, onInputChange, maxDate, todayAsMaxDate, isMarried }) => {
  const maritalStatusDateConfig: any = {
    "Married": { label: "Marriage Date", placeholder: "Select Marriage Date" },
    "Living common-law": { label: "Common-law Date", placeholder: "Select Common-law Date" },
    "Widow": { label: "Widow Date", placeholder: "Select Widow Date" },
    "Divorced": { label: "Divorce Date", placeholder: "Select Divorce Date" },
    "Separated": { label: "Separation Date", placeholder: "Select Separation Date" },
  };

  const currentStatusConfig = maritalStatusDateConfig[userData?.dec_marrige_status] || { label: "Status Changed Date", placeholder: "Select Date" };

  return (
    <div className="form-section mb-0">
      <div className="section-title">
        <i className="pi pi-user"></i>
        <h3>Personal Information</h3>
      </div>

      <div className="grid p-fluid formgrid form-max-heigth">
        {/* First Name */}
        <div className="field col-12 md:col-4">
          <label htmlFor="name">
            First Name <span className="required"></span>
          </label>
          <InputText
            value={userData.first_name || ""}
            name="first_name"
            onChange={(e) => onInputChange(e, "first_name")}
            className={`modern-input ${errors["first_name"] && "p-invalid"}`}
            placeholder="Enter first name"
          />
          <small className="p-invalid-txt">{errors["first_name"]}</small>
        </div>
        {/* middle name */}
        <div className="field col-12 md:col-4">
          <label htmlFor="name">
            Middle Name
          </label>
          <InputText
            value={userData.middle_name || ""}
            name="middle_name"
            onChange={(e) => onInputChange(e, "middle_name")}
            className={`modern-input ${errors["middle_name"] && "p-invalid"}`}
            placeholder="Enter middle name"
          />
          <small className="p-invalid-txt">{errors["middle_name"]}</small>
        </div>

        {/* Last Name */}
        <div className="field col-12 md:col-4">
          <label htmlFor="name">
            Last Name <span className="required">*</span>
          </label>
          <InputText
            value={userData.last_name || ""}
            name="last_name"
            onChange={(e) => onInputChange(e, "last_name")}
            className={`modern-input ${errors["last_name"] && "p-invalid"}`}
            placeholder="Enter last name"
          />
          <small className="p-invalid-txt">{errors["last_name"]}</small>
        </div>

        {/* Email */}
        <div className="field col-12 md:col-4">
          <label htmlFor="email">
            Email Address <span className="required">*</span>
          </label>
          <InputText
            value={userData.email || ""}
            name="email"
            onChange={(e) => onInputChange(e, "email")}
            className={errors["email"] && "p-invalid"}
            placeholder="your.email@example.com"
          />
          <small className="p-invalid-txt">{errors["email"]}</small>
        </div>

        {/* Phone Number */}
        <div className="field col-12 md:col-4">
          <label htmlFor="phone">
            Phone Number <span className="required">*</span>
          </label>
          <div className="flex-1 input-effect-div p-inputgroup">
            <span className="font-bold p-inputgroup-addon">+1</span>
            <InputText
              value={userData.phone || ""}
              name="phone"
              onChange={(e) => onInputChange(e, "phone")}
              className={errors["phone"] && "p-invalid"}
              placeholder="(123) 456-7890"
              keyfilter="pint"
              maxLength={10}
            />
            <span className="focus-bg"></span>
          </div>
          <small className="p-invalid-txt">{errors["phone"]}</small>
        </div>

        {/* sin number */}
        <div className="field col-12 md:col-4">
          <label htmlFor="sin_number">
            SIN Number <span className="required">*</span>
          </label>
          <InputText
            value={userData?.sin_number || ""}
            name="sin_number"
            onChange={(e) => onInputChange(e, "sin_number")}
            className={errors["sin_number"] && "p-invalid"}
            placeholder="123456789"
            keyfilter="pint"
            maxLength={9}
          />
          <small className="p-invalid-txt">{errors["sin_number"]}</small>
        </div>

        {/* Date of Birth */}
        <div className="field col-12 md:col-4">
          <label htmlFor="date_of_birth">
            Date of Birth <span className="required">*</span>
          </label>
          <Calendar
            value={userData.date_of_birth ? moment(userData.date_of_birth, "YYYY-MM-DD").toDate() : null}
            dateFormat="dd/mm/yy"
            onChange={(e) => onInputChange(e, "date_of_birth")}
            placeholder="Select date of birth"
            showIcon
            maxDate={maxDate}
            className={errors["date_of_birth"] ? "p-invalid" : ""}
          />
          <small className="p-invalid-txt">{errors["date_of_birth"]}</small>
        </div>

        {/* Street Address */}
        <div className="field col-12 md:col-4">
          <label htmlFor="street_address">
            Street Address <span className="required">*</span>
          </label>
          <InputTextarea
            rows={1}
            value={userData.street_address || ""}
            name="street_address"
            placeholder="Enter your complete street address"
            onChange={(e) => onInputChange(e, "street_address")}
            className={errors["street_address"] && "p-invalid"}
          />
          <small className="p-invalid-txt">{errors["street_address"]}</small>
        </div>

        {/* City */}
        <div className="field col-12 md:col-4">
          <label htmlFor="city">
            City <span className="required">*</span>
          </label>
          <InputText
            value={userData.city || ""}
            name="city"
            placeholder="Enter city"
            onChange={(e) => onInputChange(e, "city")}
            className={errors["city"] && "p-invalid"}
          />
          <small className="p-invalid-txt">{errors["city"]}</small>
        </div>

        {/* Province */}
        <div className="field col-12 md:col-4">
          <label htmlFor="province">
            Province <span className="required">*</span>
          </label>
          <Dropdown
            value={userData.province || ""}
            onChange={(e) => onInputChange(e, "province")}
            options={provinces}
            placeholder="Select province"
            className={errors["province"] ? "p-invalid" : ""}
          />
          <small className="p-invalid-txt">{errors["province"]}</small>
        </div>

        {/* Postal Code */}
        <div className="field col-12 md:col-4">
          <label htmlFor="postal_code">
            Postal Code <span className="required">*</span>
          </label>
          <InputText
            value={userData.postal_code || ""}
            name="postal_code"
            placeholder="A1A 1A1"
            onChange={(e) => onInputChange(e, "postal_code")}
            className={errors["postal_code"] && "p-invalid"}
          />
          <small className="p-invalid-txt">{errors["postal_code"]}</small>
        </div>

        <div className="field col-12 md:col-8 flex-wrap">
          <label htmlFor="dec_marrige_status">On December 31 you were :</label>
          <span className="required">*</span>
          <div className="flex gap-4 mt-2 flex-wrap">
            <div className="flex align-items-center">
              <RadioButton
                inputId="dec_status_married"
                name="dec_marrige_status"
                value="Married"
                onChange={(e) => onInputChange(e, "dec_marrige_status")}
                checked={userData?.dec_marrige_status === "Married"}
              />
              <label htmlFor="dec_status_married" className="ml-2">
                Married
              </label>
            </div>
            <div className="flex align-items-center flex-wrap">
              <RadioButton
                inputId="dec_status_common_law"
                name="dec_marrige_status"
                value="Living common-law"
                onChange={(e) => onInputChange(e, "dec_marrige_status")}
                checked={userData?.dec_marrige_status === "Living common-law"}
              />
              <label htmlFor="dec_status_common_law" className="ml-2">
                Living common-law
              </label>
            </div>
            <div className="flex align-items-center flex-wrap">
              <RadioButton
                inputId="dec_status_widow"
                name="dec_marrige_status"
                value="Widow"
                onChange={(e) => onInputChange(e, "dec_marrige_status")}
                checked={userData?.dec_marrige_status === "Widow"}
              />
              <label htmlFor="dec_status_widow" className="ml-2">
                Widow
              </label>
            </div>
            <div className="flex align-items-center flex-wrap">
              <RadioButton
                inputId="dec_status_divorced"
                name="dec_marrige_status"
                value="Divorced"
                onChange={(e) => onInputChange(e, "dec_marrige_status")}
                checked={userData?.dec_marrige_status === "Divorced"}
              />
              <label htmlFor="dec_status_divorced" className="ml-2">
                Divorced
              </label>
            </div>
            <div className="flex align-items-center flex-wrap">
              <RadioButton
                inputId="dec_status_separated"
                name="dec_marrige_status"
                value="Separated"
                onChange={(e) => onInputChange(e, "dec_marrige_status")}
                checked={userData?.dec_marrige_status === "Separated"}
              />
              <label htmlFor="dec_status_separated" className="ml-2">
                Separated
              </label>
            </div>
            <div className="flex align-items-center flex-wrap">
              <RadioButton
                inputId="dec_status_single"
                name="dec_marrige_status"
                value="Single"
                onChange={(e) => onInputChange(e, "dec_marrige_status")}
                checked={userData?.dec_marrige_status === "Single"}
              />
              <label htmlFor="dec_status_single" className="ml-2">
                Single
              </label>
            </div>
          </div>
          <small className="p-invalid-txt">{errors["dec_marrige_status"]}</small>
        </div>

        {(userData?.dec_marrige_status === "Married" || userData?.dec_marrige_status === "Living common-law") && (
          <>
            <div className="field col-12 md:col-4">
              <label htmlFor="is_spouse_canadian_resident">Is Spouse Living in Canada </label>
              <span className="required"> *</span>
              <div className="flex gap-4 mt-2">
                <div className="flex align-items-center">
                  <RadioButton
                    inputId="is_spouse_canadian_resident"
                    name="is_spouse_canadian_resident"
                    value="1"
                    onChange={(e) => onInputChange(e, "is_spouse_canadian_resident")}
                    checked={userData?.is_spouse_canadian_resident === "1"}
                  />
                  <label htmlFor="resident_yes" className="ml-2">
                    Yes
                  </label>
                </div>
                <div className="flex align-items-center">
                  <RadioButton
                    inputId="is_spouse_canadian_resident"
                    name="is_spouse_canadian_resident"
                    value="0"
                    onChange={(e) => onInputChange(e, "is_spouse_canadian_resident")}
                    checked={userData?.is_spouse_canadian_resident === "0"}
                  />
                  <label htmlFor="resident_no" className="ml-2">
                    No
                  </label>
                </div>
              </div>
              <small className="p-invalid-txt">{errors["is_spouse_canadian_resident"]}</small>
            </div>
          </>
        )}

        {(userData?.dec_marrige_status === "Married" ||
          userData?.dec_marrige_status === "Living common-law" ||
          userData?.dec_marrige_status === "Widow" ||
          userData?.dec_marrige_status === "Divorced" ||
          userData?.dec_marrige_status === "Separated") && (
            <>
              <div className="field col-12 md:col-4">
                <label htmlFor="marrige_status_changed_date">{currentStatusConfig.label} : </label>
                {(userData?.dec_marrige_status !== "Widow" && userData?.dec_marrige_status !== "Single") && <span className="required">*</span>}
                <div className="input-effect-div">
                  <Calendar
                    value={userData.marrige_status_changed_date ? moment(userData.marrige_status_changed_date, "YYYY-MM-DD").toDate() : null}
                    dateFormat="dd/mm/yy"
                    name="marrige_status_changed_date"
                    maxDate={todayAsMaxDate}
                    onChange={(e) => onInputChange(e, "marrige_status_changed_date")}
                    placeholder={currentStatusConfig.placeholder}
                    className={errors["marrige_status_changed_date"] ? "p-invalid" : ""}
                    showIcon
                  />
                  <span className="focus-bg"></span>
                </div>
                <small className="p-invalid-txt">{errors["marrige_status_changed_date"]}</small>
              </div>
            </>
          )}

        {/* Is Spouse Canadian Resident */}

        {/* SPOUSE DETAILS - Canadian Resident */}
        {(userData?.dec_marrige_status === "Married" || userData?.dec_marrige_status === "Living common-law") &&
          (userData?.is_spouse_canadian_resident === "1" || userData?.is_spouse_canadian_resident === "0") && (
            <>
              {/* Spouse First Name */}
              <div className="field col-12 md:col-4">
                <label htmlFor="spouse_first_name">
                  Spouse First Name
                  <span className="required"></span>
                </label>
                <div className="input-effect-div">
                  <InputText
                    value={userData.spouse_first_name || ""}
                    name="spouse_first_name"
                    onChange={(e) => onInputChange(e, "spouse_first_name")}
                    className={errors["spouse_first_name"] && "p-invalid"}
                    placeholder="Enter Spouse First Name"
                  />
                  <span className="focus-bg"></span>
                </div>
                <small className="p-invalid-txt">{errors["spouse_first_name"]}</small>
              </div>

              {/* Spouse Middle Name */}
              <div className="field col-12 md:col-4">
                <label htmlFor="spouse_middle_name">
                  Spouse Middle Name
                  <span className="required"></span>
                </label>
                <div className="input-effect-div">
                  <InputText
                    value={userData.spouse_middle_name || ""}
                    name="spouse_middle_name"
                    onChange={(e) => onInputChange(e, "spouse_middle_name")}
                    className={errors["spouse_middle_name"] && "p-invalid"}
                    placeholder="Enter Spouse Middle Name"
                  />
                  <span className="focus-bg"></span>
                </div>
                <small className="p-invalid-txt">{errors["spouse_middle_name"]}</small>
              </div>

              {/* Spouse Last Name */}
              <div className="field col-12 md:col-4">
                <label htmlFor="spouse_last_name">
                  Spouse Last Name
                  <span className="required">*</span>
                </label>
                <div className="input-effect-div">
                  <InputText
                    value={userData.spouse_last_name || ""}
                    name="spouse_last_name"
                    onChange={(e) => onInputChange(e, "spouse_last_name")}
                    className={errors["spouse_last_name"] && "p-invalid"}
                    placeholder="Enter Spouse Last Name"
                  />
                  <span className="focus-bg"></span>
                </div>
                <small className="p-invalid-txt">{errors["spouse_last_name"]}</small>
              </div>

              {/* Spouse DOB */}
              <div className="field col-12 md:col-4">
                <label htmlFor="spouse_dob">
                  Spouse Date of Birth
                  <span className="required">*</span>
                </label>
                <div className="input-effect-div">
                  <Calendar
                    value={userData.spouse_dob ? moment(userData.spouse_dob, "YYYY-MM-DD").toDate() : null}
                    dateFormat="dd/mm/yy"
                    maxDate={maxDate}
                    onChange={(e) => onInputChange(e, "spouse_dob")}
                    placeholder="Enter Date of Birth"
                    className={errors["spouse_dob"] ? "p-invalid" : ""}
                    showIcon
                  />
                  <span className="focus-bg"></span>
                </div>
                <small className="p-invalid-txt">{errors["spouse_dob"]}</small>
              </div>
            </>
          )}

        {/* Spouse Details - Not Canadian Resident */}
        {(userData?.dec_marrige_status === "Married" || userData?.dec_marrige_status === "Living common-law") &&
          userData?.is_spouse_canadian_resident === "0" && (
            <>
              {/* Spouse Income from Home */}
              <div className="field col-12 md:col-4">
                <label htmlFor="income_from_home">
                  Spouse Income From Home Country
                  <span className="required">*</span>
                  <>
                    <i
                      className="pi pi-info-circle"
                      data-pr-tooltip={`Convert to canadian dollar`}
                      data-pr-position="right"
                      style={{
                        marginLeft: "6px",
                        cursor: "pointer",
                        fontSize: "14px",
                      }}
                    ></i>
                    <Tooltip target=".pi-info-circle" />
                  </>
                </label>
                <div className="flex-1 input-effect-div p-inputgroup">
                  <span className="font-bold p-inputgroup-addon">$</span>
                  <InputNumber
                    value={userData.spouse_income_from_home}
                    onChange={(e) => onInputChange(e, "spouse_income_from_home")}
                    placeholder="Enter Spouse Income"
                    className={errors["spouse_income_from_home"] && "p-invalid"}
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    mode="decimal"
                  />
                </div>
                <small className="p-invalid-txt">{errors["spouse_income_from_home"]}</small>
              </div>
            </>
          )}

        {/* Spouse Details - Canadian Resident */}
        {(userData?.dec_marrige_status === "Married" || userData?.dec_marrige_status === "Living common-law") &&
          userData?.is_spouse_canadian_resident === "1" && (
            <>
              {/* spouse sin number */}
              <div className="field col-12 md:col-4">
                <label htmlFor="spouse_sin_number">Spouse SIN Number</label>
                <span className="required">*</span>
                <div className="input-effect-div">
                  <InputText
                    value={userData.spouse_sin_number || ""}
                    name="spouse_sin_number"
                    onChange={(e) => onInputChange(e, "spouse_sin_number")}
                    className={errors["spouse_sin_number"] && "p-invalid"}
                    placeholder="Enter SIN Number"
                    keyfilter="pint"
                    maxLength={9}
                  />
                  <span className="focus-bg"></span>
                </div>
                <small className="p-invalid-txt">{errors["spouse_sin_number"]}</small>
              </div>

              {/* Spouse Email */}
              <div className="field col-12 md:col-4">
                <label htmlFor="spouse_email">
                  Spouse Email <span className="required">*</span>
                </label>
                <div className="input-effect-div">
                  <InputText
                    value={userData.spouse_email || ""}
                    name="spouse_email"
                    onChange={(e) => onInputChange(e, "spouse_email")}
                    className={errors["spouse_email"] && "p-invalid"}
                    placeholder="Enter Email"
                  />
                  <span className="focus-bg"></span>
                </div>
                <small className="p-invalid-txt">{errors["spouse_email"]}</small>
              </div>

              {/* Spouse Phone */}
              <div className="field col-12 md:col-4">
                <label htmlFor="spouse_phone">
                  Spouse Phone <span className="required"></span>
                </label>
                <div className="flex-1 input-effect-div p-inputgroup">
                  <span className="font-bold p-inputgroup-addon">+1</span>
                  <InputText
                    value={userData.spouse_phone || ""}
                    name="spouse_phone"
                    onChange={(e) => onInputChange(e, "spouse_phone")}
                    className={errors["spouse_phone"] && "p-invalid"}
                    placeholder="(123) 456-7890"
                    keyfilter="pint"
                    maxLength={10}
                  />
                  <span className="focus-bg"></span>
                </div>
                <small className="p-invalid-txt">{errors["spouse_phone"]}</small>
              </div>
            </>
          )}
      </div>
    </div>
  );
};

export default PersonalSection;
